import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Undo2, Trash2, PlusCircle, FolderPlus, Folder, Pencil, FileText, Plus, AlertTriangle } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const WorkScopeRequirementsPage = ({ activeSubTab, onWorkScopeChange, onActiveTabChange }) => {
  const [workScopes, setWorkScopes] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [models, setModels] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showAddWorkScopeModal, setShowAddWorkScopeModal] = useState(false);
  const [showEditWorkScopeModal, setShowEditWorkScopeModal] = useState(false);
  const [editingWorkScope, setEditingWorkScope] = useState(null);
  const [newWorkScope, setNewWorkScope] = useState({
    name: '',
    description: '',
    scopes: [],
    required_documents: [],
    is_dopuszczajacy: false,
    is_kierujacy: false,
  });

  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState("models");
  const [selectedModels, setSelectedModels] = useState([]);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [newOrGroup, setNewOrGroup] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    work_scope: null,
    required_documents: [],
    required_doc_groups_category: []
  });
  const [initialWorkScopeDocuments, setInitialWorkScopeDocuments] = useState([]);
  const [searchScopes, setSearchScopes] = useState('');
  const [searchModels, setSearchModels] = useState('');
  const [searchDocuments, setSearchDocuments] = useState('');
  const [searchGroupDocuments, setSearchGroupDocuments] = useState('');

  // Synchronizacja activeType z activeSubTab
  useEffect(() => {
    if (!loading) {
      if (activeSubTab === 'authorization-levels') {
        setActiveType(null);
      } else if (activeSubTab && activeSubTab.startsWith('authorization-')) {
        const workScopeId = parseInt(activeSubTab.split('-')[1]);
        const workScopeExists = workScopes.some((scope) => scope.id === workScopeId);
        if (workScopeExists && workScopeId !== activeType) {
          setActiveType(workScopeId);
        } else if (!workScopeExists) {
          setActiveType(null);
          if (onActiveTabChange) {
            onActiveTabChange('authorization-levels');
          }
        }
      } else {
        setActiveType(null);
      }
    }
  }, [activeSubTab, workScopes, onActiveTabChange, loading]);

  // Pobieranie danych początkowych
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Pobranie Work Scopes
        const workScopesResponse = await fetch(`${API_BASE_URL}/workscopes/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!workScopesResponse.ok) {
          const errorText = await workScopesResponse.text();
          throw new Error(`Failed to fetch work scopes: ${workScopesResponse.status} - ${errorText.substring(0, 200)}`);
        }
        const workScopesData = await workScopesResponse.json();
        setWorkScopes(workScopesData);

        // Pobranie Qualification Types
        const documentsResponse = await fetch(`${API_BASE_URL}/qualificationtype/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!documentsResponse.ok) {
          const errorText = await documentsResponse.text();
          throw new Error(`Failed to fetch qualificationtype: ${documentsResponse.status} - ${errorText.substring(0, 200)}`);
        }
        const documentsData = await documentsResponse.json();
        setDocuments(documentsData);

        // Pobranie Models
        const modelsResponse = await fetch(`${API_BASE_URL}/models/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!modelsResponse.ok) {
          const errorText = await modelsResponse.text();
          throw new Error(`Failed to fetch models: ${modelsResponse.status} - ${errorText.substring(0, 200)}`);
        }
        const modelsData = await modelsResponse.json();
        setModels(modelsData);

        // Pobranie Scope of Works
        const scopeOfWorksResponse = await fetch(`${API_BASE_URL}/scopeofworks/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!scopeOfWorksResponse.ok) {
          const errorText = await scopeOfWorksResponse.text();
          throw new Error(`Failed to fetch scope of works: ${scopeOfWorksResponse.status} - ${errorText.substring(0, 200)}`);
        }
        const scopeOfWorksData = await scopeOfWorksResponse.json();
        setScopes(scopeOfWorksData);

      } catch (error) {
        console.error('API Error:', error);
        if (error.message.includes('JSON')) {
          setError('Authentication failed. Please refresh the page and try again.');
        } else {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Pobieranie kategorii dla aktywnego workScope
  useEffect(() => {
    if (activeType) {
      const fetchWorkScopeDetails = async () => {
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/workscopes/${activeType}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error('Failed to fetch work scope details');
          const workScopeDetails = await response.json();
          setInitialWorkScopeDocuments(workScopeDetails.required_documents || []);
          setNewCategory((prev) => ({ ...prev, work_scope: activeType }));
        } catch (error) {
          setError(error.message);
        }
      };

      const fetchCategories = async () => {
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/categories/?work_scope=${activeType}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error('Failed to fetch categories');
          const categoriesData = await response.json();
          setCategories(categoriesData);
        } catch (error) {
          setError(error.message);
        }
      };

      fetchWorkScopeDetails();
      fetchCategories();
    }
  }, [activeType]);

  // Handlery
  const handleAddWorkScope = async () => {
    if (!newWorkScope.name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/workscopes/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newWorkScope.name,
          description: newWorkScope.description,
          required_documents: newWorkScope.required_documents || [],
          is_dopuszczajacy: newWorkScope.is_dopuszczajacy,
          is_kierujacy: newWorkScope.is_kierujacy,
          scopes: newWorkScope.scopes || []
        })
      });
      if (!response.ok) throw new Error('Failed to add work scope');
      const data = await response.json();
      // Optymistyczna aktualizacja workScopes
      setWorkScopes(prev => [...prev, data]);
      setShowAddWorkScopeModal(false);
      setNewWorkScope({ name: '', description: '', required_documents: [], scopes: [], is_dopuszczajacy: false, is_kierujacy: false });
      setActiveType(data.id);
      setError(null);
      // Najpierw nawigacja, potem odświeżanie
      if (onActiveTabChange) onActiveTabChange(`authorization-${data.id}`);
      // Odroczone odświeżanie workScopes
      setTimeout(() => {
        if (onWorkScopeChange) onWorkScopeChange();
      }, 100);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpdateWorkScope = async () => {
    if (!editingWorkScope.name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/workscopes/${editingWorkScope.id}/`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingWorkScope.name,
          description: editingWorkScope.description,
          required_documents: editingWorkScope.required_documents || [],
          scopes: editingWorkScope.scopes || [],
          is_dopuszczajacy: editingWorkScope.is_dopuszczajacy,
          is_kierujacy: editingWorkScope.is_kierujacy
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update work scope error:', response.status, errorText);
        throw new Error(`Failed to update work scope: ${response.status} - ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setWorkScopes(prev => prev.map(ws => ws.id === data.id ? data : ws));
      setShowEditWorkScopeModal(false);
      setEditingWorkScope(null);
      setError(null);
      if (onWorkScopeChange) onWorkScopeChange();
    } catch (error) {
      console.error('Update work scope error:', error);
      if (error.message.includes('JSON')) {
        setError('Authentication failed. Please refresh the page and try again.');
      } else {
        setError(error.message);
      }
    }
  };

  const handleDeleteWorkScope = async (workScopeId) => {
    if (!window.confirm('Are you sure you want to delete this work scope?')) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/workscopes/${workScopeId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete work scope');
      setWorkScopes(prev => prev.filter(ws => ws.id !== workScopeId));
      const remainingScopes = workScopes.filter(ws => ws.id !== workScopeId);
      if (remainingScopes.length > 0) {
        setActiveType(remainingScopes[0].id);
        if (onActiveTabChange) onActiveTabChange(`authorization-${remainingScopes[0].id}`);
      } else {
        setActiveType(null);
        if (onActiveTabChange) onActiveTabChange('authorization-levels');
      }
      setError(null);
      if (onWorkScopeChange) onWorkScopeChange();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCheckboxChange = (documentId) => {
    setNewWorkScope((prev) => ({
      ...prev,
      required_documents: prev.required_documents.includes(documentId)
        ? prev.required_documents.filter((id) => id !== documentId)
        : [...prev.required_documents, documentId]
    }));
  };

  const handleScopeChange = (scopeId) => {
    setNewWorkScope(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scopeId)
        ? prev.scopes.filter(id => id !== scopeId)
        : [...prev.scopes, scopeId]
    }));
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }
    if (selectedModels.length === 0) {
      setError('At least one model must be selected');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const requestData = {
        ...newCategory,
        models: selectedModels,
        work_scope: activeType,
        required_doc_groups_category: newCategory.required_doc_groups_category || []
      };
      const response = await fetch(`${API_BASE_URL}/categories/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      if (!response.ok) throw new Error('Failed to create category');
      const data = await response.json();
      setCategories(prev => [...prev, data]);
      setShowAddCategoryModal(false);
      setSelectedModels([]);
      setNewCategory({ name: '', description: '', work_scope: null, required_documents: [], required_doc_groups_category: [] });
      setError(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingItem.name.trim()) {
      setError('Category name is required');
      return;
    }
    if (!editingItem.models || editingItem.models.length === 0) {
      setError('At least one model must be selected');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/categories/${editingItem.id}/`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingItem.name,
          description: editingItem.description,
          work_scope: editingItem.work_scope,
          required_documents: editingItem.required_documents || [],
          required_doc_groups_category: editingItem.required_doc_groups_category || [],
          models: editingItem.models || []
        })
      });
      if (!response.ok) throw new Error('Failed to update category');
      const data = await response.json();
      setCategories(prev => prev.map(category => category.id === data.id ? data : category));
      setShowEditCategoryModal(false);
      setEditingItem(null);
      setError(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_BASE_URL}/categories/${categoryId}/`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
      });
      if (!response.ok) throw new Error("Failed to delete category");
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      setError(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteGroup = (index) => {
    if (!window.confirm('Are you sure you want to delete this alternative group?')) return;
    if (showEditCategoryModal) {
      setEditingItem((prev) => ({
        ...prev,
        required_doc_groups_category: prev.required_doc_groups_category.filter((_, i) => i !== index),
      }));
    } else {
      setNewCategory((prev) => ({
        ...prev,
        required_doc_groups_category: prev.required_doc_groups_category.filter((_, i) => i !== index),
      }));
    }
  };

  const handleCategoryCheckboxChange = (documentId) => {
    setNewCategory((prev) => ({
      ...prev,
      required_documents: prev.required_documents.includes(documentId)
        ? prev.required_documents.filter((id) => id !== documentId)
        : [...prev.required_documents, documentId]
    }));
  };

  const handleOrGroupCheckboxChange = (documentId) => {
    setNewOrGroup((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]
    );
  };

  const startEditingGroup = (index) => {
    if (showEditCategoryModal) {
      setNewOrGroup(editingItem.required_doc_groups_category[index]);
      setEditingGroupIndex(index);
      setShowAddGroupModal(true);
    } else {
      setNewOrGroup(newCategory.required_doc_groups_category[index]);
      setEditingGroupIndex(index);
      setShowAddGroupModal(true);
    }
  };

  const addOrGroup = () => {
    if (newOrGroup.length < 2) {
      setError('At least two documents are required for a group');
      return;
    }
    if (showEditCategoryModal) {
      setEditingItem((prev) => {
        const updatedGroups = [...(prev.required_doc_groups_category || [])];
        if (editingGroupIndex !== null) {
          updatedGroups[editingGroupIndex] = newOrGroup;
        } else {
          updatedGroups.push(newOrGroup);
        }
        return { ...prev, required_doc_groups_category: updatedGroups };
      });
    } else {
      setNewCategory((prev) => {
        const updatedGroups = [...(prev.required_doc_groups_category || [])];
        if (editingGroupIndex !== null) {
          updatedGroups[editingGroupIndex] = newOrGroup;
        } else {
          updatedGroups.push(newOrGroup);
        }
        return { ...prev, required_doc_groups_category: updatedGroups };
      });
    }
    setNewOrGroup([]);
    setEditingGroupIndex(null);
    setShowAddGroupModal(false);
    setError(null);
  };

  const renderCategory = (category) => {
    const inheritedDocs = activeWorkScope?.required_documents || [];
    const additionalDocs = (category.required_documents || []).filter(
      (docId) => !inheritedDocs.includes(docId)
    );
    const alternativeGroups = category.required_doc_groups_category || [];
    const categoryModels = category.models || [];

    return (
      <AccordionItem value={category.id} key={category.id} className="border border-steel-200 rounded-lg overflow-hidden">
        <AccordionTrigger className="px-3 py-2 bg-gradient-to-r from-steel-50 to-white hover:no-underline data-[state=open]:border-b data-[state=open]:border-steel-200">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-brand-100 rounded-md flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
                  <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
                  <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
                  <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
                </svg>
              </div>
              <div className="text-left">
                <h4 className="text-sm font-semibold text-graphite-900">{category.name}</h4>
                {category.description && (
                  <p className="text-[10px] text-graphite-500">{category.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              <button
                className="p-1 rounded-md text-graphite-500 hover:bg-steel-100 hover:text-brand-600 transition-colors"
                onClick={() => {
                  setEditingItem({ ...category });
                  setShowEditCategoryModal(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                className="p-1 rounded-md text-graphite-500 hover:bg-red-50 hover:text-red-600 transition-colors mr-1"
                onClick={() => handleDeleteCategory(category.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="data-[state=closed]:h-0 data-[state=closed]:p-0 data-[state=open]:p-3">
          <div className="space-y-3">
            {/* Selected Models */}
            {categoryModels.length > 0 ? (
              <div>
                <h5 className="text-[10px] font-medium text-graphite-500 uppercase tracking-wide mb-1.5">Selected Models</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                  {categoryModels.map((modelId) => {
                    const model = models.find((m) => m.id === modelId);
                    return model ? (
                      <div key={modelId} className="bg-brand-50 border border-brand-100 rounded px-2 py-1 flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600 flex-shrink-0">
                          <path d="m9 11 3 3L22 4" />
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        <span className="text-xs font-medium text-graphite-900 truncate">{model.name}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-steel-50 rounded px-3 py-2 text-center">
                <p className="text-graphite-500 text-xs">No models selected for this platform.</p>
              </div>
            )}

            {/* Additional Requirements */}
            {additionalDocs.length > 0 && (
              <div>
                <h5 className="text-[10px] font-medium text-graphite-500 uppercase tracking-wide mb-1.5">Additional Requirements</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                  {additionalDocs.map((docId) => {
                    const document = documents.find((d) => d.id === docId);
                    return document ? (
                      <div key={docId} className="bg-green-50 border border-green-100 rounded px-2 py-1 flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 flex-shrink-0">
                          <path d="m9 11 3 3L22 4" />
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        <span className="text-xs font-medium text-graphite-900 truncate">{document.name}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Alternative Groups */}
            {alternativeGroups.length > 0 && (
              <div>
                <h5 className="text-[10px] font-medium text-graphite-500 uppercase tracking-wide mb-1.5">Group Requirements (One of each)</h5>
                <div className="space-y-2">
                  {alternativeGroups.map((group, index) => (
                    <div key={index} className="bg-purple-50 border border-purple-100 rounded p-2">
                      <p className="text-[10px] font-medium text-purple-700 mb-1.5">Group {index + 1}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                        {group.map((docId) => {
                          const document = documents.find((d) => d.id === docId);
                          return document ? (
                            <div key={docId} className="bg-white border border-purple-100 rounded px-2 py-1 flex items-center gap-1.5">
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600 flex-shrink-0">
                                <path d="m9 11 3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                              </svg>
                              <span className="text-xs font-medium text-graphite-900 truncate">{document.name}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  const activeWorkScope = workScopes.find(ws => ws.id === activeType);

  return (
    <div className="flex flex-col h-screen overflow-y-hidden">
      <div className="flex flex-col gap-2 px-4 py-3 bg-white border-b border-steel-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="text-lg font-bold text-graphite-900">Authorization levels</h1>
          <div className="flex space-x-2 mt-2 md:mt-0">
            <Button
              onClick={() => setShowAddWorkScopeModal(true)}
              className="px-3 py-1.5 text-sm rounded-full flex items-center bg-brand-500 text-white hover:bg-brand-600"
            >
              <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
              Add new authorization level
            </Button>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-4" style={{ height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        {workScopes.length === 0 || activeSubTab === 'authorization-levels' ? (
          <div className="flex flex-col items-center justify-center py-8 bg-steel-50 rounded-lg">
            <h3 className="text-sm font-medium text-graphite-900 mb-1">No authorization levels available</h3>
            <p className="text-xs text-graphite-500 text-center max-w-md mb-3">
              Start by adding your first authorization level to configure the system.
            </p>
            <Button
              onClick={() => setShowAddWorkScopeModal(true)}
              className="px-3 py-1.5 text-sm rounded-full flex items-center bg-white border border-steel-200 text-graphite-900 hover:bg-steel-50"
            >
              <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
              Add first authorization level
            </Button>
          </div>
        ) : activeWorkScope ? (
          <div className="space-y-3">
            {/* Main Authorization Level Header */}
            <div className="bg-white rounded-xl border border-steel-200 overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-2.5 border-b border-brand-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-brand-100 rounded-lg flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-sm font-bold text-graphite-900">{activeWorkScope.name}</h1>
                      {activeWorkScope.description && (
                        <p className="text-xs text-graphite-500">{activeWorkScope.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      className="p-1.5 rounded-lg text-graphite-500 hover:bg-steel-100 hover:text-brand-600 transition-colors"
                      onClick={() => {
                        setEditingWorkScope({ ...activeWorkScope });
                        setShowEditWorkScopeModal(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="p-1.5 rounded-lg text-graphite-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                      onClick={() => handleDeleteWorkScope(activeWorkScope.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {(() => {
              const categoryModels = new Set();
              (categories || []).forEach(category => {
                (category.models || []).forEach(modelId => categoryModels.add(modelId));
              });
              const availableModels = models.filter(model => !categoryModels.has(model.id));
              return availableModels.length > 0 ? (
                <div className="bg-white rounded-xl border border-red-200 overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-red-50 to-white px-4 py-2.5 border-b border-red-100">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-graphite-900">Turbine Models Not Assigned</h3>
                        <p className="text-xs text-graphite-500">These models need to be assigned to a platform</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {availableModels.map((model) => (
                        <div key={model.id} className="bg-red-50 border border-red-100 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                          <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0" />
                          <span className="text-xs font-medium text-graphite-900 truncate">{model.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Minimum Requirements */}
            <div className="bg-white rounded-xl border border-steel-200 overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-green-50 to-white px-4 py-2.5 border-b border-green-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-graphite-900">Minimum Requirements</h3>
                    <p className="text-xs text-graphite-500">Required documents for all platforms</p>
                  </div>
                </div>
              </div>
              <div className="p-3">
                {activeWorkScope.required_documents && activeWorkScope.required_documents.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {activeWorkScope.required_documents
                      .map((docId) => documents.find((d) => d.id === docId))
                      .filter((document) => document)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((document) => (
                        <div key={document.id} className="bg-green-50 border border-green-100 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 flex-shrink-0">
                            <path d="m9 11 3 3L22 4"></path>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                          </svg>
                          <span className="text-xs font-medium text-graphite-900 truncate">{document.name}</span>
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <div className="bg-steel-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-graphite-500 text-xs">No required documents for this authorization level.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Scope of Works */}
            <div className="bg-white rounded-xl border border-steel-200 overflow-hidden shadow-sm">
              <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-2.5 border-b border-blue-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-graphite-900">Scope of Works</h3>
                    <p className="text-xs text-graphite-500">Allowed work types for this authorization</p>
                  </div>
                </div>
              </div>
              <div className="p-3">
                {activeWorkScope.scopes && activeWorkScope.scopes.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {activeWorkScope.scopes.map((scopeId) => {
                      const matchedScope = scopes.find((s) => s.id === scopeId);
                      return matchedScope ? (
                        <div key={scopeId} className="bg-blue-50 border border-blue-100 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 flex-shrink-0">
                            <path d="m9 11 3 3L22 4"></path>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                          </svg>
                          <span className="text-xs font-medium text-graphite-900 truncate">{matchedScope.name}</span>
                        </div>
                      ) : (
                        <div key={scopeId} className="bg-red-50 border border-red-100 rounded-lg px-2 py-1.5 text-red-500 text-xs">
                          Scope ID {scopeId} not found
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-steel-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-graphite-500 text-xs">No scopes assigned to this authorization level.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Platforms */}
            {activeWorkScope && categories ? (
              <div className="bg-white rounded-xl border border-steel-200 overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-2.5 border-b border-brand-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-brand-100 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
                          <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
                          <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
                          <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-graphite-900">Platforms</h3>
                        <p className="text-xs text-graphite-500">Turbine model categories and requirements</p>
                      </div>
                    </div>
                    <button
                      className="px-2.5 py-1.5 rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors flex items-center gap-1"
                      onClick={() => {
                        setNewCategory(prev => ({ ...prev, work_scope: activeType }));
                        setShowAddCategoryModal(true);
                      }}
                      disabled={!activeType}
                      title="Add Platform"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Add</span>
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  {categories.filter(cat => cat.work_scope === activeWorkScope.id).length > 0 ? (
                    <Accordion type="single" collapsible className="space-y-2">
                      {categories
                        .filter(category => category.work_scope === activeWorkScope.id)
                        .map(category => renderCategory(category))}
                    </Accordion>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 bg-steel-50 rounded-lg">
                      <div className="w-8 h-8 bg-steel-100 rounded-lg flex items-center justify-center mb-2">
                        <FolderPlus className="h-4 w-4 text-graphite-400" />
                      </div>
                      <h3 className="text-sm font-medium text-graphite-900 mb-1">No platforms defined</h3>
                      <p className="text-xs text-graphite-500 mb-3 text-center max-w-md">
                        Start by adding platforms to create authorizations system.
                      </p>
                      <Button
                        onClick={() => {
                          setNewCategory(prev => ({ ...prev, work_scope: activeWorkScope.id }));
                          setShowAddCategoryModal(true);
                        }}
                        className="px-3 py-1.5 text-sm rounded-full flex items-center bg-brand-500 text-white hover:bg-brand-600"
                      >
                        <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                        Add First Platform
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 bg-steel-50 rounded-lg">
                <h3 className="text-sm font-medium text-graphite-900 mb-1">No authorization levels available</h3>
                <p className="text-xs text-graphite-500 text-center max-w-md mb-3">
                  Start by adding your first authorization level.
                </p>
                <Button
                  onClick={() => setShowAddWorkScopeModal(true)}
                  className="px-3 py-1.5 text-sm rounded-full flex items-center bg-white border border-steel-200 text-graphite-900 hover:bg-steel-50"
                >
                  <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                  Add first authorization level
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Modal: Add Work Scope (Boczny panel) */}
{showAddWorkScopeModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
    <div className="bg-white w-full max-w-4xl h-full flex flex-col shadow-xl">
      <div className="px-6 py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-white">
        <h2 className="text-xl font-bold text-graphite-900 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
            <PlusCircle className="h-4 w-4 text-brand-600" />
          </div>
          Add new authorization level
        </h2>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-xl border border-steel-200 overflow-hidden">
            <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
              <h3 className="text-sm font-semibold text-graphite-900 flex items-center gap-2">
                <div className="w-6 h-6 bg-brand-100 rounded-md flex items-center justify-center">
                  <FileText className="h-3.5 w-3.5 text-brand-600" />
                </div>
                Basic Information
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="level-name" className="text-xs font-medium text-graphite-500 uppercase tracking-wide">
                      Name *
                    </Label>
                    <Input
                      id="level-name"
                      type="text"
                      placeholder="Enter authorization level name"
                      maxLength={50}
                      className="mt-1 w-full p-2.5 border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={newWorkScope.name}
                      onChange={(e) => setNewWorkScope((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-graphite-500 uppercase tracking-wide">Permissions</Label>
                    <div className="mt-2 flex gap-3">
                      <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                        newWorkScope.is_kierujacy ? 'border-brand-500 bg-brand-50' : 'border-steel-200 hover:border-steel-300'
                      }`}>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={newWorkScope.is_kierujacy || false}
                          onChange={(e) => setNewWorkScope((prev) => ({ ...prev, is_kierujacy: e.target.checked }))}
                        />
                        <div className={`w-4 h-4 rounded flex items-center justify-center ${newWorkScope.is_kierujacy ? 'bg-brand-500' : 'bg-steel-200'}`}>
                          {newWorkScope.is_kierujacy && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <span className="text-sm font-medium text-graphite-700">Supervisor</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                        newWorkScope.is_dopuszczajacy ? 'border-brand-500 bg-brand-50' : 'border-steel-200 hover:border-steel-300'
                      }`}>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={newWorkScope.is_dopuszczajacy || false}
                          onChange={(e) => setNewWorkScope((prev) => ({ ...prev, is_dopuszczajacy: e.target.checked }))}
                        />
                        <div className={`w-4 h-4 rounded flex items-center justify-center ${newWorkScope.is_dopuszczajacy ? 'bg-brand-500' : 'bg-steel-200'}`}>
                          {newWorkScope.is_dopuszczajacy && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <span className="text-sm font-medium text-graphite-700">Preparer</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="level-description" className="text-xs font-medium text-graphite-500 uppercase tracking-wide">
                    Description
                  </Label>
                  <Textarea
                    id="level-description"
                    placeholder="Enter a description for this authorization level"
                    rows="4"
                    className="mt-1 w-full p-2.5 border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    value={newWorkScope.description || ''}
                    onChange={(e) => setNewWorkScope((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Scopes of Work Card */}
          <div className="bg-white rounded-xl border border-steel-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-3 border-b border-blue-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-graphite-900 flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                  <FileText className="h-3.5 w-3.5 text-blue-600" />
                </div>
                Scopes of Work
              </h3>
              <Input
                type="text"
                placeholder="Search..."
                className="w-40 p-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchScopes}
                onChange={(e) => setSearchScopes(e.target.value)}
              />
            </div>
            <div className="p-4">
              {scopes.length === 0 ? (
                <p className="text-graphite-500 text-sm">Loading scopes...</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {scopes
                    .filter((scope) => scope.name.toLowerCase() !== 'other')
                    .filter((scope) => scope.name.toLowerCase().includes(searchScopes.toLowerCase()))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((scope) => {
                      const isChecked = newWorkScope.scopes?.includes(scope.id) || false;
                      return (
                        <label key={scope.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                          isChecked ? 'border-blue-500 bg-blue-50' : 'border-steel-200 hover:border-steel-300'
                        }`}>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isChecked}
                            onChange={() => {
                              setNewWorkScope((prev) => ({
                                ...prev,
                                scopes: isChecked ? prev.scopes.filter((id) => id !== scope.id) : [...prev.scopes, scope.id],
                              }));
                            }}
                          />
                          <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${isChecked ? 'bg-blue-500' : 'bg-steel-200'}`}>
                            {isChecked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <span className="text-sm text-graphite-700">{scope.name}</span>
                        </label>
                      );
                    })}
                </div>
              )}
              {scopes.filter((scope) => scope.name.toLowerCase() !== 'other').filter((scope) => scope.name.toLowerCase().includes(searchScopes.toLowerCase())).length === 0 && scopes.length > 0 && (
                <p className="text-graphite-500 text-sm text-center py-4">No matching scopes found.</p>
              )}
            </div>
          </div>

          {/* Required Documents Card */}
          <div className="bg-white rounded-xl border border-steel-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-white px-4 py-3 border-b border-green-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-graphite-900 flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                  </svg>
                </div>
                Required Documents
              </h3>
              <Input
                type="text"
                placeholder="Search..."
                className="w-40 p-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchDocuments}
                onChange={(e) => setSearchDocuments(e.target.value)}
              />
            </div>
            <div className="p-4">
              {documents.length === 0 ? (
                <p className="text-graphite-500 text-sm">Loading documents...</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {documents
                    .filter((doc) => doc.name.toLowerCase().includes(searchDocuments.toLowerCase()))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((doc) => {
                      const isChecked = newWorkScope.required_documents?.includes(doc.id) || false;
                      return (
                        <label key={doc.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                          isChecked ? 'border-green-500 bg-green-50' : 'border-steel-200 hover:border-steel-300'
                        }`}>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isChecked}
                            onChange={() => {
                              setNewWorkScope((prev) => ({
                                ...prev,
                                required_documents: isChecked ? prev.required_documents.filter((id) => id !== doc.id) : [...prev.required_documents, doc.id],
                              }));
                            }}
                          />
                          <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${isChecked ? 'bg-green-500' : 'bg-steel-200'}`}>
                            {isChecked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <span className="text-sm text-graphite-700">{doc.name}</span>
                        </label>
                      );
                    })}
                </div>
              )}
              {documents.filter((doc) => doc.name.toLowerCase().includes(searchDocuments.toLowerCase())).length === 0 && documents.length > 0 && (
                <p className="text-graphite-500 text-sm text-center py-4">No matching documents found.</p>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-steel-200 bg-steel-50 flex items-center justify-between">
        <div className="text-sm text-graphite-500">
          {newWorkScope.scopes?.length || 0} scopes · {newWorkScope.required_documents?.length || 0} documents selected
        </div>
        <div className="flex gap-3">
          <Button
            className="px-5 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-100 transition-colors"
            onClick={() => {
              setShowAddWorkScopeModal(false);
              setError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            className="px-5 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 transition-colors flex items-center gap-2"
            onClick={handleAddWorkScope}
          >
            <PlusCircle className="h-4 w-4" />
            Add authorization level
          </Button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Modal: Edit Work Scope */}
      {showEditWorkScopeModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
          <div className="bg-white w-full max-w-4xl h-full flex flex-col shadow-xl">
            <div className="px-6 py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-white">
              <h2 className="text-xl font-bold text-graphite-900 flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                  <Pencil className="h-4 w-4 text-brand-600" />
                </div>
                Edit authorization level
              </h2>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="space-y-5">
                {/* Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Lewa kolumna: Name i Checkboxy */}
                  <div className="space-y-8">
                    {/* Name */}
                    <div>
                      <Label
                        htmlFor="edit-level-name"
                        className="block text-sm font-medium text-graphite-900 mb-2"
                      >
                        Name
                      </Label>
                      <Input
                        id="edit-level-name"
                        type="text"
                        placeholder="Enter work scope name - max 50 chars."
                        maxLength={50}
                        className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                        value={editingWorkScope?.name || ''}
                        onChange={(e) =>
                          setEditingWorkScope((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>
                    {/* Checkboxy */}
                    <div className="flex space-x-6">
                      <div className="flex items-center">
                        <input
                          id="is-kierujacy"
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={editingWorkScope?.is_kierujacy || false}
                          onChange={(e) =>
                            setEditingWorkScope((prev) => ({
                              ...prev,
                              is_kierujacy: e.target.checked,
                            }))
                          }
                        />
                        <Label htmlFor="is-kierujacy" className="ml-2 text-sm text-graphite-700">
                          Supervisor
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="is-dopuszczajacy"
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={editingWorkScope?.is_dopuszczajacy || false}
                          onChange={(e) =>
                            setEditingWorkScope((prev) => ({
                              ...prev,
                              is_dopuszczajacy: e.target.checked,
                            }))
                          }
                        />
                        <Label htmlFor="is-dopuszczajacy" className="ml-2 text-sm text-graphite-700">
                          Preparer
                        </Label>
                      </div>
                    </div>
                  </div>
                  {/* Prawa kolumna: Description */}
                  <div>
                    <Label
                      htmlFor="edit-level-description"
                      className="block text-sm font-medium text-graphite-900 mb-2"
                    >
                      Description
                    </Label>
                    <Textarea
                      id="edit-level-description"
                      placeholder="Enter work scope description"
                      rows="4"
                      className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                      value={editingWorkScope?.description || ''}
                      onChange={(e) =>
                        setEditingWorkScope((prev) => ({ ...prev, description: e.target.value }))
                      }
                    />
                  </div>
                </div>
                {/* Scopes of Work */}
                <div className="border-t " >
                  <div className="flex items-center justify-between mt-4 mb-2">
                    <Label className="text-sm font-medium text-graphite-900">
                      Scopes of Work
                    </Label>
                    <Input
                      type="text"
                      placeholder="Search scopes..."
                      className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                      value={searchScopes}
                      onChange={(e) => setSearchScopes(e.target.value)}
                    />
                  </div>
                  {scopes.length === 0 ? (
                    <p className="text-graphite-500">Loading scopes...</p>
                  ) : (
                    <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {scopes
                          .filter((scope) => scope.name.toLowerCase() !== 'other')
                          .filter((scope) =>
                            scope.name.toLowerCase().includes(searchScopes.toLowerCase())
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((scope) => {
                            const isChecked = editingWorkScope?.scopes?.includes(scope.id) || false;
                            return (
                              <div key={scope.id} className="flex items-center">
                                <input
                                  id={`edit-scope-${scope.id}`}
                                  type="checkbox"
                                  className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                                  checked={isChecked}
                                  onChange={() => {
                                    setEditingWorkScope((prev) => ({
                                      ...prev,
                                      scopes: isChecked
                                        ? prev.scopes.filter((id) => id !== scope.id)
                                        : [...prev.scopes, scope.id],
                                    }));
                                  }}
                                />
                                <Label
                                  htmlFor={`edit-scope-${scope.id}`}
                                  className="ml-2 text-sm text-graphite-700"
                                >
                                  {scope.name}
                                </Label>
                              </div>
                            );
                          })}
                      </div>
                      {scopes
                        .filter((scope) => scope.name.toLowerCase() !== 'other')
                        .filter((scope) =>
                          scope.name.toLowerCase().includes(searchScopes.toLowerCase())
                        ).length === 0 && (
                          <p className="text-graphite-500">No matching scopes found.</p>
                        )}
                    </div>
                  )}
                </div>
                {/* Required Documents */}
                <div className="border-t">
                  <div className="flex items-center justify-between mt-4 mb-2">
                    <Label className="text-sm font-medium text-graphite-900">
                      Required Documents
                    </Label>
                    <Input
                      type="text"
                      placeholder="Search documents..."
                      className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                      value={searchDocuments}
                      onChange={(e) => setSearchDocuments(e.target.value)}
                    />
                  </div>
                  {documents.length === 0 ? (
                    <p className="text-graphite-500">Loading documents...</p>
                  ) : (
                    <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {documents
                          .filter((doc) =>
                            doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((doc) => {
                            const isChecked =
                              editingWorkScope?.required_documents?.includes(doc.id) || false;
                            return (
                              <div key={doc.id} className="flex items-center">
                                <input
                                  id={`edit-doc-${doc.id}`}
                                  type="checkbox"
                                  className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                                  checked={isChecked}
                                  onChange={() => {
                                    setEditingWorkScope((prev) => ({
                                      ...prev,
                                      required_documents: isChecked
                                        ? prev.required_documents.filter((id) => id !== doc.id)
                                        : [...prev.required_documents, doc.id],
                                    }));
                                  }}
                                />
                                <Label
                                  htmlFor={`edit-doc-${doc.id}`}
                                  className="ml-2 text-sm text-graphite-700"
                                >
                                  {doc.name}
                                </Label>
                              </div>
                            );
                          })}
                      </div>
                      {documents
                        .filter((doc) =>
                          doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                        ).length === 0 && (
                          <p className="text-graphite-500">No matching documents found.</p>
                        )}
                    </div>
                  )}
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
              </div>
            </div>
            <div className="p-6 border-t border-steel-300 flex justify-end space-x-4">
              <Button
                className="px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
                onClick={() => {
                  setShowEditWorkScopeModal(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="px-6 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600"
                onClick={handleUpdateWorkScope}
              >
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Category */}
{showAddCategoryModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
    <div className="bg-white w-full max-w-4xl h-full flex flex-col shadow-xl">
      <div className="px-6 py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-white">
        <h2 className="text-xl font-bold text-graphite-900 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
            <FolderPlus className="h-4 w-4 text-brand-600" />
          </div>
          Add new platform
        </h2>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lewa kolumna: Name */}
            <div>
              <Label
                htmlFor="category-name"
                className="block text-sm font-medium text-graphite-900 mb-2"
              >
                Name
              </Label>
              <Input
                id="category-name"
                type="text"
                placeholder="Platform name - max 50 chars."
                maxLength={50}
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            {/* Prawa kolumna: Description */}
            <div>
              <Label
                htmlFor="category-description"
                className="block text-sm font-medium text-graphite-900 mb-2"
              >
                Description
              </Label>
              <Textarea
                id="category-description"
                placeholder="Platform description"
                rows="4"
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={newCategory.description || ''}
                onChange={(e) =>
                  setNewCategory((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>
          {/* Models */}
          <div className="border-t">
            <div className="flex items-center justify-between mt-4 mb-2">
              <Label className="text-sm font-medium text-graphite-900">
                Models
              </Label>
              <Input
                type="text"
                placeholder="Search models..."
                className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchModels}
                onChange={(e) => setSearchModels(e.target.value)}
              />
            </div>
            {models.length === 0 ? (
              <p className="text-graphite-500">Loading models...</p>
            ) : (
              <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {models
                    .filter((model) =>
                      model.name.toLowerCase().includes(searchModels.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((model) => (
                      <div key={model.id} className="flex items-center">
                        <input
                          id={`model-${model.id}`}
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={selectedModels.includes(model.id)}
                          onChange={() => {
                            setSelectedModels((prev) =>
                              prev.includes(model.id)
                                ? prev.filter((id) => id !== model.id)
                                : [...prev, model.id]
                            );
                          }}
                        />
                        <Label
                          htmlFor={`model-${model.id}`}
                          className="ml-2 text-sm text-graphite-700"
                        >
                          {model.name}
                        </Label>
                      </div>
                    ))}
                </div>
                {models
                  .filter((model) =>
                    model.name.toLowerCase().includes(searchModels.toLowerCase())
                  ).length === 0 && (
                    <p className="text-graphite-500">No matching models found.</p>
                  )}
              </div>
            )}
          </div>
          {/* Additional Required Documents */}
          <div className="border-t">
            <div className="flex items-center justify-between mt-4 mb-2">
              <Label className="text-sm font-medium text-graphite-900">
                Additional Required Documents
              </Label>
              <Input
                type="text"
                placeholder="Search documents..."
                className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchDocuments}
                onChange={(e) => setSearchDocuments(e.target.value)}
              />
            </div>
            {documents.length === 0 ? (
              <p className="text-graphite-500">Loading documents...</p>
            ) : (
              <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {documents
                    .filter((doc) => !activeWorkScope?.required_documents?.includes(doc.id))
                    .filter((doc) =>
                      doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((doc) => (
                      <div key={doc.id} className="flex items-center">
                        <input
                          id={`category-doc-${doc.id}`}
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={newCategory.required_documents?.includes(doc.id) || false}
                          onChange={() => handleCategoryCheckboxChange(doc.id)}
                        />
                        <Label
                          htmlFor={`category-doc-${doc.id}`}
                          className="ml-2 text-sm text-graphite-700"
                        >
                          {doc.name}
                        </Label>
                      </div>
                    ))}
                </div>
                {documents
                  .filter((doc) => !activeWorkScope?.required_documents?.includes(doc.id))
                  .filter((doc) =>
                    doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                  ).length === 0 && (
                    <p className="text-graphite-500">
                      No matching documents found or all documents are already required by the authorization level.
                    </p>
                  )}
              </div>
            )}
          </div>
          {/* Alternative Document Groups */}
          <div className="border-t">
            <Label className="block text-sm font-medium text-graphite-900 mt-4 mb-2">
              Alternative Document Groups
            </Label>
            {newCategory.required_doc_groups_category?.length > 0 ? (
              <div className="space-y-2">
                {newCategory.required_doc_groups_category.map((group, index) => (
                  <div
                    key={index}
                    className="p-4 bg-steel-50 rounded-md relative border border-steel-300"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-medium text-sm text-graphite-900">
                        Group {index + 1}
                      </p>
                      <div className="flex items-center">
                        <button
                          className="p-1 rounded-full text-graphite-500 hover:bg-steel-100 mr-1"
                          onClick={() => startEditingGroup(index)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-square-pen h-4 w-4"
                          >
                            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path>
                          </svg>
                        </button>
                        <button
                          className="p-1 rounded-full text-graphite-500 hover:bg-steel-100"
                          onClick={() => handleDeleteGroup(index)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-trash2 h-4 w-4"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {group
                        .map((docId) => documents.find((d) => d.id === docId))
                        .filter((doc) => doc)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((doc) => (
                          <div key={doc.id} className="flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="h-3 w-3 text-graphite-500 mr-2"
                            >
                              <circle cx="12" cy="12" r="5"></circle>
                            </svg>
                            <span className="text-sm text-graphite-700">{doc.name}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-graphite-500">No alternative groups defined.</p>
            )}
            <Button
              className="mt-2 px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
              onClick={() => {
                setNewOrGroup([]);
                setEditingGroupIndex(null);
                setShowAddGroupModal(true);
              }}
            >
              Add Alternative Group
            </Button>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </div>
      <div className="p-6 border-t border-steel-300 flex justify-end space-x-4">
        <Button
          className="px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
          onClick={() => {
            setShowAddCategoryModal(false);
            setError(null);
            setNewCategory({ name: '', description: '', work_scope: null, required_documents: [], required_doc_groups_category: [] });
            setSelectedModels([]);
            setSearchModels('');
            setSearchDocuments('');
            setSearchGroupDocuments('');
          }}
        >
          Cancel
        </Button>
        <Button
          className="px-6 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600"
          onClick={handleAddCategory}
        >
          Add platform
        </Button>
      </div>
    </div>
  </div>
)}

{/* Modal: Edit Category */}
{showEditCategoryModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
    <div className="bg-white w-full max-w-4xl h-full flex flex-col shadow-xl">
      <div className="px-6 py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-white">
        <h2 className="text-xl font-bold text-graphite-900 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
            <Folder className="h-4 w-4 text-brand-600" />
          </div>
          Edit platform
        </h2>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lewa kolumna: Name */}
            <div>
              <Label
                htmlFor="edit-category-name"
                className="block text-sm font-medium text-graphite-900 mb-2"
              >
                Name
              </Label>
              <Input
                id="edit-category-name"
                type="text"
                placeholder="Platform name - max 50 chars."
                maxLength={50}
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={editingItem?.name || ''}
                onChange={(e) =>
                  setEditingItem((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            {/* Prawa kolumna: Description */}
            <div>
              <Label
                htmlFor="edit-category-description"
                className="block text-sm font-medium text-graphite-900 mb-2"
              >
                Description
              </Label>
              <Textarea
                id="edit-category-description"
                placeholder="Platform description"
                rows="4"
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={editingItem?.description || ''}
                onChange={(e) =>
                  setEditingItem((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>
          {/* Models */}
          <div className="border-t">
            <div className="flex items-center justify-between mt-4 mb-2">
              <Label className="text-sm font-medium text-graphite-900">
                Models
              </Label>
              <Input
                type="text"
                placeholder="Search models..."
                className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchModels}
                onChange={(e) => setSearchModels(e.target.value)}
              />
            </div>
            {models.length === 0 ? (
              <p className="text-graphite-500">Loading models...</p>
            ) : (
              <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {models
                    .filter((model) =>
                      model.name.toLowerCase().includes(searchModels.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((model) => (
                      <div key={model.id} className="flex items-center">
                        <input
                          id={`edit-model-${model.id}`}
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={editingItem?.models?.includes(model.id) || false}
                          onChange={() => {
                            setEditingItem((prev) => ({
                              ...prev,
                              models: prev.models.includes(model.id)
                                ? prev.models.filter((id) => id !== model.id)
                                : [...prev.models, model.id],
                            }));
                          }}
                        />
                        <Label
                          htmlFor={`edit-model-${model.id}`}
                          className="ml-2 text-sm text-graphite-700"
                        >
                          {model.name}
                        </Label>
                      </div>
                    ))}
                </div>
                {models
                  .filter((model) =>
                    model.name.toLowerCase().includes(searchModels.toLowerCase())
                  ).length === 0 && (
                    <p className="text-graphite-500">No matching models found.</p>
                  )}
              </div>
            )}
          </div>
          {/* Additional Required Documents */}
          <div className="border-t">
            <div className="flex items-center justify-between mt-4 mb-2">
              <Label className="text-sm font-medium text-graphite-900">
                Additional Required Documents
              </Label>
              <Input
                type="text"
                placeholder="Search documents..."
                className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchDocuments}
                onChange={(e) => setSearchDocuments(e.target.value)}
              />
            </div>
            {documents.length === 0 ? (
              <p className="text-graphite-500">Loading documents...</p>
            ) : (
              <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {documents
                    .filter((doc) => !activeWorkScope?.required_documents?.includes(doc.id))
                    .filter((doc) =>
                      doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((doc) => (
                      <div key={doc.id} className="flex items-center">
                        <input
                          id={`edit-category-doc-${doc.id}`}
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={editingItem?.required_documents?.includes(doc.id) || false}
                          onChange={() => {
                            setEditingItem((prev) => ({
                              ...prev,
                              required_documents: prev.required_documents.includes(doc.id)
                                ? prev.required_documents.filter((id) => id !== doc.id)
                                : [...prev.required_documents, doc.id],
                            }));
                          }}
                        />
                        <Label
                          htmlFor={`edit-category-doc-${doc.id}`}
                          className="ml-2 text-sm text-graphite-700"
                        >
                          {doc.name}
                        </Label>
                      </div>
                    ))}
                </div>
                {documents
                  .filter((doc) => !activeWorkScope?.required_documents?.includes(doc.id))
                  .filter((doc) =>
                    doc.name.toLowerCase().includes(searchDocuments.toLowerCase())
                  ).length === 0 && (
                    <p className="text-graphite-500">
                      No matching documents found or all documents are already required by the authorization level.
                    </p>
                  )}
              </div>
            )}
          </div>
          {/* Alternative Document Groups */}
          <div className="border-t">
            <Label className="block text-sm font-medium text-graphite-900 mt-4 mb-2">
              Alternative Document Groups
            </Label>
            {editingItem?.required_doc_groups_category?.length > 0 ? (
              <div className="space-y-2">
                {editingItem.required_doc_groups_category.map((group, index) => (
                  <div
                    key={index}
                    className="p-4 bg-steel-50 rounded-md relative border border-steel-300"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-medium text-sm text-graphite-900">
                        Group {index + 1}
                      </p>
                      <div className="flex items-center">
                        <button
                          className="p-1 rounded-full text-graphite-500 hover:bg-steel-100 mr-1"
                          onClick={() => startEditingGroup(index)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-square-pen h-4 w-4"
                          >
                            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path>
                          </svg>
                        </button>
                        <button
                          className="p-1 rounded-full text-graphite-500 hover:bg-steel-100"
                          onClick={() => handleDeleteGroup(index)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-trash2 h-4 w-4"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {group
                        .map((docId) => documents.find((d) => d.id === docId))
                        .filter((doc) => doc)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((doc) => (
                          <div key={doc.id} className="flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="h-3 w-3 text-graphite-500 mr-2"
                            >
                              <circle cx="12" cy="12" r="5"></circle>
                            </svg>
                            <span className="text-sm text-graphite-700">{doc.name}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-graphite-500">No alternative groups defined.</p>
            )}
            <Button
              className="mt-2 px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
              onClick={() => {
                setNewOrGroup([]);
                setEditingGroupIndex(null);
                setShowAddGroupModal(true);
              }}
            >
              Add Alternative Group
            </Button>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </div>
      <div className="p-6 border-t border-steel-300 flex justify-end space-x-4">
        <Button
          className="px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
          onClick={() => {
            setShowEditCategoryModal(false);
            setError(null);
            setEditingItem(null);
            setSearchModels('');
            setSearchDocuments('');
            setSearchGroupDocuments('');
          }}
        >
          Cancel
        </Button>
        <Button
          className="px-6 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600"
          onClick={handleUpdateCategory}
        >
          Save changes
        </Button>
      </div>
    </div>
  </div>
)}

{/* Modal: Add/Edit Alternative Group */}
{showAddGroupModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
    <div className="bg-white w-full max-w-4xl h-full flex flex-col shadow-xl">
      <div className="px-6 py-5 border-b border-brand-200 bg-gradient-to-r from-brand-50 to-white">
        <h2 className="text-xl font-bold text-graphite-900 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
            <FileText className="h-4 w-4 text-brand-600" />
          </div>
          {editingGroupIndex !== null ? 'Edit alternative group' : 'Add alternative group'}
        </h2>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-graphite-900">
                Select Documents (at least 2)
              </Label>
              <Input
                type="text"
                placeholder="Search documents..."
                className="w-48 p-2 text-sm border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={searchGroupDocuments}
                onChange={(e) => setSearchGroupDocuments(e.target.value)}
              />
            </div>
            {documents.length === 0 ? (
              <p className="text-graphite-500">Loading documents...</p>
            ) : (
              <div className="border border-steel-300 rounded-md p-4 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {documents
                    .filter((doc) => {
                      const workScopeDocs = activeWorkScope?.required_documents || [];
                      const categoryDocs = showEditCategoryModal
                        ? editingItem?.required_documents || []
                        : newCategory.required_documents || [];
                      return !workScopeDocs.includes(doc.id) && !categoryDocs.includes(doc.id);
                    })
                    .filter((doc) =>
                      doc.name.toLowerCase().includes(searchGroupDocuments.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((doc) => (
                      <div key={doc.id} className="flex items-center">
                        <input
                          id={`group-doc-${doc.id}`}
                          type="checkbox"
                          className="h-4 w-4 text-brand-500 border-steel-400 rounded focus:ring-brand-500"
                          checked={newOrGroup.includes(doc.id)}
                          onChange={() => handleOrGroupCheckboxChange(doc.id)}
                        />
                        <Label
                          htmlFor={`group-doc-${doc.id}`}
                          className="ml-2 text-sm text-graphite-700"
                        >
                          {doc.name}
                        </Label>
                      </div>
                    ))}
                </div>
                {documents
                  .filter((doc) => {
                    const workScopeDocs = activeWorkScope?.required_documents || [];
                    const categoryDocs = showEditCategoryModal
                      ? editingItem?.required_documents || []
                      : newCategory.required_documents || [];
                    return !workScopeDocs.includes(doc.id) && !categoryDocs.includes(doc.id);
                  })
                  .filter((doc) =>
                    doc.name.toLowerCase().includes(searchGroupDocuments.toLowerCase())
                  ).length === 0 && (
                    <p className="text-graphite-500">
                      No matching documents found or all documents are already required by the authorization level or category.
                    </p>
                  )}
              </div>
            )}
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </div>
      <div className="p-6 border-t border-steel-300 flex justify-end space-x-4">
        <Button
          className="px-6 py-2 border border-steel-300 rounded-full text-graphite-700 hover:bg-steel-50"
          onClick={() => {
            setShowAddGroupModal(false);
            setError(null);
            setNewOrGroup([]);
            setEditingGroupIndex(null);
            setSearchGroupDocuments('');
          }}
        >
          Cancel
        </Button>
        <Button
          className="px-6 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600"
          onClick={addOrGroup}
        >
          {editingGroupIndex !== null ? 'Update group' : 'Add group'}
        </Button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default WorkScopeRequirementsPage;